<template>
  <div class="md:m-auto md:w-11/12 md:max-w-[1216px]">
    <div :class="['px-4 md:m-auto md:px-0', spacious ? 'pb-20' : 'pb-12 md:pb-20']">
      <div class="mx-auto grid w-full grid-cols-1 md:grid-cols-5 md:gap-2 lg:grid-cols-5 lg:gap-2">
        <div
          v-for="category in categories"
          :key="category.label"
          class="group relative h-[160px] w-full cursor-pointer overflow-hidden md:aspect-[2/3] md:h-auto"
          @click="handleCategoryClick(category)"
        >
          <img class="h-full w-full object-cover" :alt="category.label" :src="isSP ? category.imageSP : category.imagePC" />
          <div class="pointer-events-none absolute inset-0"></div>
          <span
            class="pointer-events-none absolute inset-0 flex items-end justify-center pb-[20px] font-Spectral text-2xl font-normal tracking-[0.5px] text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)] md:text-[20px] md:leading-9"
          >
            {{ capitalizeFirstLetter(category.label) }}<span aria-hidden="true" class="ml-2 md:hidden">›</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import destinationsPC from '@/wabunka-src/common/assets/img/top/destinations_pc.png';
import destinationsSP from '@/wabunka-src/common/assets/img/top/destinations_sp.png';
import experiencesPC from '@/wabunka-src/common/assets/img/top/experiences_pc.png';
import experiencesSP from '@/wabunka-src/common/assets/img/top/experiences_sp.png';
import gastronomyPC from '@/wabunka-src/common/assets/img/top/gastronomy_pc.png';
import gastronomySP from '@/wabunka-src/common/assets/img/top/gastronomy_sp.png';
import journeyPC from '@/wabunka-src/common/assets/img/top/journey_pc.png';
import journeySP from '@/wabunka-src/common/assets/img/top/journey_sp.png';
import stayPC from '@/wabunka-src/common/assets/img/top/stays_pc.png';
import staySP from '@/wabunka-src/common/assets/img/top/stays_sp.png';
import { useIsSP } from '@/wabunka-src/common/composables/general/useIsSP/useIsSP';
import { sendGAWabunkaClickTopCategoryCardEvent } from '@/wabunka-src/common/utils/googleAnalytics/events/clickTopCategoryCard';
import { TOP_CATEGORY_CARD_NAMES } from '@/wabunka-src/constants/topCategoryCardName';

interface Category {
  label: (typeof TOP_CATEGORY_CARD_NAMES)[keyof typeof TOP_CATEGORY_CARD_NAMES];
  imagePC: string;
  imageSP: string;
  href: string;
  isExternal?: boolean;
}

interface Props {
  /** Top variant の新デザイン余白(SP=80px)を使う。未指定時は従来余白(SP=48px)。A/B の control は未指定で旧据え置き */
  spacious?: boolean;
}

withDefaults(defineProps<Props>(), { spacious: false });

const isSP = useIsSP();

const categories: Category[] = [
  { label: TOP_CATEGORY_CARD_NAMES.DESTINATION, imagePC: destinationsPC, imageSP: destinationsSP, href: '/#areas' },
  { label: TOP_CATEGORY_CARD_NAMES.JOURNEY, imagePC: journeyPC, imageSP: journeySP, href: 'https://lp.wabunka-lux.jp/tailoredjapantravel', isExternal: true },
  { label: TOP_CATEGORY_CARD_NAMES.STAY, imagePC: stayPC, imageSP: staySP, href: '/lp/experiential-stay/' },
  { label: TOP_CATEGORY_CARD_NAMES.EXPERIENCE, imagePC: experiencesPC, imageSP: experiencesSP, href: '/s/experiences/' },
  {
    label: TOP_CATEGORY_CARD_NAMES.GASTRONOMY,
    imagePC: gastronomyPC,
    imageSP: gastronomySP,
    href: '/s/experiences/?themes=kaiseki,vegetarian-cuisine,sushi,fermentation,confectionary,sake-brewery-tasting,cooking-class,tea,other-cuisine',
  },
];

/**
 * 文字列の先頭を大文字にする
 * @param str 文字列
 * @returns 大文字にした文字列
 */
const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const handleCategoryClick = (category: Category) => {
  sendGAWabunkaClickTopCategoryCardEvent(category.label);
  if (category.isExternal) {
    window.open(category.href, '_blank');
  } else {
    navigateTo(category.href);
  }
};
</script>
